import { skipToken } from '@reduxjs/toolkit/query'
import { UserContext } from 'core/contexts/UserContext'
import { useGetUtilisateursByClientQuery } from 'lauria/api/utilisateurs'
import {
  SelectedTag,
  SelectedUtilisateur,
} from 'lauria/types/selectedUtilisateur'
import { UtilisateurListItem } from 'lauria/types/utilisateur'
import { getUtilisateurDisplayLabel } from 'lauria/utils/utilisateurLabel'
import { ChangeEvent, Dispatch, SetStateAction, useContext, useMemo, useState } from 'react'

export type UtilisateurSelectOption = {
  value: string
  label: string
}

type UseUtilisateurMultiSelectParams = {
  selectedTags: SelectedTag[]
  onSelect: Dispatch<SetStateAction<SelectedUtilisateur[]>>
  isUtilisateurExcluded: (utilisateur: UtilisateurListItem) => boolean
}

export const useUtilisateurMultiSelect = ({
  selectedTags,
  onSelect,
  isUtilisateurExcluded,
}: UseUtilisateurMultiSelectParams) => {
  const { user: currentUser } = useContext(UserContext)
  const [selectValue, setSelectValue] = useState('')

  const idClient = currentUser?.idClient ?? null
  const tagIds = useMemo(
    () => selectedTags.map((tag) => tag.id),
    [selectedTags]
  )

  const queryArg =
    idClient !== null && tagIds.length > 0
      ? { idClient, params: { tagIds, itemsPerPage: 100 } }
      : skipToken

  const { data, isLoading } = useGetUtilisateursByClientQuery(queryArg)
  const utilisateurs = data?.member ?? []

  const options = useMemo<UtilisateurSelectOption[]>(() => {
    return utilisateurs
      .filter((utilisateur) => !isUtilisateurExcluded(utilisateur))
      .map((utilisateur) => ({
        value: utilisateur.idUtilisateur.toString(),
        label: getUtilisateurDisplayLabel(utilisateur),
      }))
  }, [utilisateurs, isUtilisateurExcluded])

  const handleSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value

    if (selectedValue === '') {
      return
    }

    const utilisateurId = Number(selectedValue)
    const selectedOption = options.find(
      (option) => option.value === selectedValue
    )

    if (!selectedOption) {
      return
    }

    onSelect((prev) => {
      const alreadySelected = prev.some((item) => item.id === utilisateurId)

      if (alreadySelected) {
        return prev
      }

      return [
        ...prev,
        {
          id: utilisateurId,
          label: selectedOption.label,
        },
      ]
    })

    setSelectValue('')
  }

  return {
    options,
    isLoading,
    selectValue,
    handleSelect,
  }
}
